import React, { Component, createRef, FormEvent, RefObject } from 'react';
import { Form as FormikForm, Formik, FormikProps } from 'formik';
import { MutationResult, MutationFunction } from 'react-apollo';
import { Divider, Grid } from '@material-ui/core';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { compose } from 'recompose';
import { find } from 'lodash';
import moment from 'moment';

import {
  ContentCommentary,
  Network,
  ShareContentDestination,
  ShareContentInput,
  SharedContentItem,
  SharedStatus,
  SharesItem,
  ShareType,
  ShareContentComponent,
  ShareContentDocument,
  GetSharePreviewInfoQuery,
} from 'api';
import {
  IWithContent,
  IWithNotify,
  IWithApolloClient,
  IWithUser,
  IWithSelectedNetworks,
  withApolloClient,
  withContent,
  withNotify,
  withUser,
  withSelectedNetworks,
} from 'providers';
import { IWithSocialConnections, withSocialConnections } from 'providers/queries';
import { FACEBOOK_LIMIT } from 'constants/index';

import { IShare, IShareNetworkAuth } from '../../types';
import { SharingSchema } from '../../validations';
import { FACEBOOK_ID } from 'features/posting/constants';

import Scheduler from '../Scheduler';
import ShareButton from '../ShareButton';
import Preview from '../Preview';
import Networks from '../Networks';

import Heading from './Heading';

interface IProps {
  previewInfo: GetSharePreviewInfoQuery['getSharePreviewInfo'];
  onConnect: () => void;
  onClose: () => void;
}

interface IPropsEnhanced
  extends IProps,
    IWithContent,
    IWithSocialConnections,
    IWithApolloClient,
    IWithNotify,
    IWithUser,
    IWithSelectedNetworks,
    WithStyles<typeof styles> {}

interface IState {
  working: boolean;
  values: IShare;
}

interface IVariables {
  content_id: string;
  items: ShareContentInput[];
  share_session_id?: string;
}

const styles = (theme: Theme) =>
  createStyles({
    container: {
      paddingTop: theme.spacing(4),
    },
    content: {
      position: 'relative',
      paddingLeft: theme.spacing(10),
      paddingRight: theme.spacing(10),
      paddingTop: theme.spacing(5),
      backgroundColor: theme.palette.background.default,
    },
    divider: {
      height: 3,
    },
  });

const enhancers = compose<IPropsEnhanced, IProps>(
  withContent,
  withNotify,
  withSocialConnections('none'),
  withApolloClient,
  withUser,
  withSelectedNetworks,
  withStyles(styles),
);

// TODO: This entire file needs to be refactored, cleaned up, and broken up
class Form extends Component<IPropsEnhanced, IState> {
  container: RefObject<HTMLDivElement>;
  state: IState = {
    working: false,
    values: null,
  };

  constructor(props: IPropsEnhanced) {
    super(props);
    this.container = createRef();
  }

  getAccountId(provider: Network) {
    const { connections, selectedNetworks } = this.props;
    const hasSelectedNetworks = selectedNetworks && !!selectedNetworks.length;
    const defaultAccount = connections.find(conn => conn.provider === provider);

    if (!hasSelectedNetworks) {
      return defaultAccount && defaultAccount.id;
    }

    const selectedAccount = selectedNetworks.find(selectedNetwork => {
      if (!selectedNetwork || typeof selectedNetwork === 'string') {
        return false;
      }
      return selectedNetwork.provider === provider;
    });

    if (typeof selectedAccount === 'string') {
      return undefined;
    }

    if (!selectedAccount) {
      return defaultAccount && defaultAccount.id;
    }

    return selectedAccount && selectedAccount.id;
  }

  getCommentary(suggested: ContentCommentary) {
    const { user, content } = this.props;

    const isOwner = !!content.user && content.user.id === user.id;
    const commentary = ((suggested && suggested.commentary) || '').trim();
    const { body } = content.content_object;

    if (isOwner) {
      return commentary || body || '';
    }

    return commentary || '';
  }

  getInitialValues(): IShare {
    const { content } = this.props;

    const shareType = ShareType.Now;
    const shareAt: IShare['share_at'] = null;
    const shareSessionId: SharesItem['share_session_id'] = null;

    return {
      content_id: content.content_id,
      share_type: shareType,
      share_at: shareAt,
      twitter: this.getNetworkInitialValues(Network.Twitter, shareSessionId),
      linkedin: this.getNetworkInitialValues(Network.Linkedin, shareSessionId),
      facebook: this.getFacebookInitialValues(shareSessionId),
    };
  }

  getNetworkInitialValues = (network: Network, shareSessionId: string): IShareNetworkAuth => {
    const { previewInfo, selectedNetworks } = this.props;
    const { allowed_counts, suggested_commentary } = previewInfo;

    const suggested = find(suggested_commentary, { network });
    const leadingAccountId = this.getAccountId(network);
    const needsNewShare = !shareSessionId && suggested && leadingAccountId;
    const hasSelectedNetworks = !!selectedNetworks.length;

    const networkInitialValues: IShareNetworkAuth = {
      active: false,
      limit: allowed_counts[network],
      network_id: leadingAccountId,
      user_commentary: '',
      share_session_id: shareSessionId,
    };

    if (hasSelectedNetworks) {
      networkInitialValues.active = !!find(selectedNetworks, ['id', leadingAccountId]);
      networkInitialValues.user_commentary = this.getCommentary(suggested);
    } else if (needsNewShare) {
      networkInitialValues.active = true;
      networkInitialValues.user_commentary = this.getCommentary(suggested);
    }

    return networkInitialValues;
  };

  getFacebookInitialValues = (shareSessionId: string) => {
    const { previewInfo, selectedNetworks } = this.props;
    const { suggested_commentary } = previewInfo;
    const suggested = find(suggested_commentary, { network: Network.Facebook });

    return {
      active: selectedNetworks && selectedNetworks.includes(FACEBOOK_ID),
      limit: FACEBOOK_LIMIT,
      user_commentary: this.getCommentary(suggested),
      share_session_id: shareSessionId,
    };
  };

  handleError = () => {
    const { notify } = this.props;
    this.setState({ working: false, values: null });
    notify.mutationError();
  };

  handleCompleted = (data: MutationResult['data']) => {
    const { notify, onClose } = this.props;
    const { values } = this.state;

    const { share_type, share_at } = values;
    const { shareContent } = data;
    const items: SharedContentItem[] = shareContent.results;
    const hasFailure = items.some(item => item.status === SharedStatus.Failed);

    if (hasFailure) {
      notify.error({ i18n: { i18nKey: 'sharing:shareFailure' } });
    } else {
      const i18nKey =
        share_type === ShareType.Now ? 'sharing:shareSuccess' : 'sharing:scheduledSuccess';
      const date = moment.unix(share_at).format('dddd, MMMM Do, YYYY [at] h:mma');

      notify.info({
        i18n: {
          i18nKey,
          values: { date },
        },
      });
    }

    this.setState({ working: false, values: null }, onClose);
  };

  shareToTwitterAndLinkedin = (shareContent: MutationFunction, values: IShare) => {
    const { content_id, share_at, share_type, twitter, linkedin } = values;
    const items: ShareContentInput[] = [];
    const common = { share_type, share_at };
    let shareSessionId: string = null;

    if (twitter.active) {
      const { user_commentary, network_id, share_session_id } = twitter;
      items.push({ user_commentary, network_id, ...common });
      shareSessionId = share_session_id;
    }

    if (linkedin.active) {
      const { user_commentary, network_id, share_session_id } = linkedin;
      items.push({ user_commentary, network_id, ...common });
      shareSessionId = share_session_id;
    }

    if (!items.length) {
      return;
    }

    const variables: IVariables = {
      content_id,
      items,
      share_session_id: shareSessionId,
    };

    this.setState({ working: true, values }, () =>
      shareContent({
        variables,
      }),
    );
  };

  shareToFacebook = (values: IShare) => {
    const { apolloClient } = this.props;
    const { content_id, facebook } = values;
    if (!facebook.active) {
      return;
    }
    const { previewInfo } = this.props;
    const { shortened_urls } = previewInfo;
    const { user_commentary } = facebook;

    if (!shortened_urls) {
      return;
    }

    window.FB &&
      window.FB.ui({
        method: 'share',
        href: `${shortened_urls.direct.short_link}`,
        quote: user_commentary,
      });

    const variables = {
      content_id,
      items: [
        {
          share_type: ShareType.Frontend,
          destination: ShareContentDestination.Facebook,
        },
      ],
    };

    apolloClient.mutate({
      mutation: ShareContentDocument,
      variables,
    });
  };

  handleSubmit = (shareContent: MutationFunction) => (values: IShare) => {
    this.shareToTwitterAndLinkedin(shareContent, values);
    this.shareToFacebook(values);
  };

  render() {
    const { classes, content, onConnect, previewInfo } = this.props;
    const { working } = this.state;

    const initialValues = this.getInitialValues();
    const isInitialValid = SharingSchema(initialValues).isValidSync(initialValues);
    const suggestedTime = previewInfo.suggested_share_time;
    const scheduledTime = initialValues.share_at;

    return (
      <ShareContentComponent onError={this.handleError} onCompleted={this.handleCompleted}>
        {shareContent => (
          <Formik
            enableReinitialize
            onSubmit={this.handleSubmit(shareContent)}
            initialValues={initialValues}
            isInitialValid={isInitialValid}
            validationSchema={SharingSchema()}>
            {({ isValid, handleSubmit }: FormikProps<IShare>) => {
              const formProps = {
                onSubmit: (event: FormEvent<HTMLFormElement>) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleSubmit(event);
                },
              };

              return (
                <FormikForm {...formProps}>
                  <Heading shareCount={content.share_count} previewInfo={previewInfo} />
                  <Grid container className={classes.container} spacing={3}>
                    <Grid lg={8} xs={12} item>
                      <Networks onConnect={onConnect} previewInfo={previewInfo} />
                      <Scheduler suggestedTime={suggestedTime} scheduledTime={scheduledTime} />
                    </Grid>
                    <Grid lg={4} xs={12} item>
                      <Preview />
                    </Grid>
                  </Grid>
                  <Divider className={classes.divider} />
                  <Grid container justify="flex-end">
                    <Grid item>
                      <ShareButton i18nKey="common:share" disabled={!isValid} working={working} />
                    </Grid>
                  </Grid>
                </FormikForm>
              );
            }}
          </Formik>
        )}
      </ShareContentComponent>
    );
  }
}

export default enhancers(Form);
